import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PortfolioHeading from '../../../../components/PortfolioHeading';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import PortfolioDealsEmptyState from 'components/PortfolioDealsEmptyState';
import { BuyerListingStats, PortfolioQueryParams } from '../../models';
import { PF_GRID_VIEW_LIMIT } from '../../../data';
import useMultiSort from 'hooks/useMultiSort';
import { ViewSelect } from '../../../../components/ViewSelect';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import DataRoomInvitations from './components/DataRoomInvitations';
import ProspectiveBuyer from './components/ProspectiveBuyer';
import { useLockScrollOnParamsReroute } from 'hooks/useLockScrollOnParamsReroute';
import BuyerDeals from './components/BuyerDeals';
import StarIcon from 'icons/Star.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import useRefetch from 'hooks/useRefetch';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import { Button } from 'ncoded-component-library';
import { useNavigate } from 'react-router-dom';
import MultiSortPanel from 'components/MultiSortPanel';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useBuyerDealsFilterOptions from './components/BuyerDeals/useBuyerDealsFilterOptions';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import { ASCENDING_DESCENDING_COPY } from 'models/Sort';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/BuyerDeals/components/BuyerDealTableCells/data';
import { getHasActiveFilters, getFilterOptionByValue } from 'utils';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import {
  DEAL_TO_FILTER_KEY,
  EBIDTA_TO_FILTER_KEY,
} from '../../../../components/PortfolioSellerFilters/data';
import { LOCATION_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import { INDUSTRY_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';
import { REQUIRED_COLUMN } from './components/TableCells/data';

import './BuyerBrowsePortfolio.styles.scss';

const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.price]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.ebitda]: ASCENDING_DESCENDING_COPY,
};

type BuyerBrowsePortfolioProps = {
  className?: string;
};

const BuyerBrowsePortfolio: React.FC<BuyerBrowsePortfolioProps> = (props) => {
  const { className } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    currentUser: { isGridViewForDeals, hideBuyerPortfolioColumns },
    isRWCMember,
  } = useContext(CurrentUserContext);

  const [isEmptyDeals, setIsEmptyDeals] = useState(false);
  const [isEmptyDRInvitation, setIsEmptyDRInvitation] = useState(false);
  const [isEmptyPrivateInvitations, setIsEmptyPrivateInvitations] =
    useState(false);
  const [buyerListingStats, setBuyerListingStats] =
    useState<BuyerListingStats>();

  const { refetchFlag, refetch } = useRefetch();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const classes = classNames('buyer-browse-portfolio', className);

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const {
    params,
    params: { timeframe },
  } = useQueryParams<PortfolioQueryParams>();

  const navigate = useNavigate();

  const isPhablet = useMatchMedia('isPhablet');

  useLockScrollOnParamsReroute();

  const isTableView = useMemo(() => !isGridViewForDeals, [isGridViewForDeals]);

  const buyerTableColumns = useMemo(
    () =>
      TABLE_COLUMNS.filter((col) => !hideBuyerPortfolioColumns.includes(col)),
    [hideBuyerPortfolioColumns],
  );

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    buyerTableColumns,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const buyerDealsFilterOptions = useBuyerDealsFilterOptions(buyerListingStats);

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onColumnSizingChange,
    onReorder,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(TABLES_WITH_COLUMN_SETTINGS.BUYER, TABLE_COLUMNS);

  const buyerDealsColumnFilterOptions = useMemo(
    () => ({
      price: {
        options: getFilterOptionByValue(
          buyerDealsFilterOptions,
          DEAL_TO_FILTER_KEY,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.price],
      },
      ebitda: {
        options: getFilterOptionByValue(
          buyerDealsFilterOptions,
          EBIDTA_TO_FILTER_KEY,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.ebitda],
      },
      state: {
        options: getFilterOptionByValue(
          buyerDealsFilterOptions,
          LOCATION_FILTER_KEY,
        ),
      },
      industry: {
        options: getFilterOptionByValue(
          buyerDealsFilterOptions,
          INDUSTRY_FILTER_KEY,
        ),
      },
    }),
    [buyerDealsFilterOptions, multiSortDirectionTranslation],
  );

  const isEmptyPage = useMemo(
    () => isEmptyDeals && isEmptyDRInvitation && isEmptyPrivateInvitations,
    [isEmptyDRInvitation, isEmptyDeals, isEmptyPrivateInvitations],
  );

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(buyerDealsFilterOptions, params),
    [buyerDealsFilterOptions, params],
  );

  const dataLimit = useMemo(
    () => `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
    [isTableView, limit],
  );

  useEffect(() => {
    (async () => {
      try {
        const stats = await api.portfolio.buyer.buyerListingStats({
          timeframe,
        });

        const newStats = {
          ...stats.data,
          all: `${Object.values(stats.data).reduce((acc, el) => acc + +el, 0)}`,
        };

        setBuyerListingStats(newStats);
      } catch (err) {
        popServerError(err);
      }
    })();
  }, [refetchFlag, timeframe, popServerError]);

  return (
    <div className={classes}>
      <div className="browse-portfolio-heading">
        <div className="browse-portfolio-heading__left">
          <PortfolioHeading />
        </div>

        {(!isEmptyDeals || hasActiveFilters) && (
          <div className="browse-portfolio-heading__right">
            {isTableView && !isEmptyDeals && (
              <NumberOfResultsPerPageFilter
                limit={limit}
                updateLimit={updateLimit}
              />
            )}
            <MultiFilterPanel
              ref={multiFilterRef}
              filters={buyerDealsFilterOptions}
            />
            <MultiSortPanel
              ref={multiSortRef}
              options={multiSortValues}
              sortDirectionTranslations={multiSortDirectionTranslation}
            />
            {isTableView && (
              <ManageColumns
                allColumns={allColumns}
                hiddenColumns={hiddenColumns}
                headingTranslations={HEADING_TRANSLATIONS}
                lockedColumn={REQUIRED_COLUMN}
                isDefaultColumns={isDefaultColumns}
                onSave={saveColumnSettings}
                onResetToDefault={resetToDefault}
              />
            )}
            <ViewSelect
              updateProp="isGridViewForDeals"
              isTableView={isTableView}
            />
            {!isRWCMember && (
              <Button
                className="watchlist-button"
                icon={StarIcon}
                onClick={() => navigate('watchlist')}
              >
                {!isPhablet && t('shortlist')}
              </Button>
            )}
          </div>
        )}
      </div>

      <ActiveFilterSortPills
        filterOptions={buyerDealsFilterOptions}
        sortOptions={multiSortValues}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <BuyerDeals
        isTableView={isTableView}
        dataLimit={dataLimit}
        isEmpty={isEmptyDeals}
        columnFilterOptions={buyerDealsColumnFilterOptions}
        columns={columns}
        columnSizing={columnSizing}
        setIsEmptyDeals={setIsEmptyDeals}
        refetch={refetch}
        onReorder={onReorder}
        onColumnSizingChange={onColumnSizingChange}
      />

      <ProspectiveBuyer
        isTableView={isTableView}
        isEmpty={isEmptyPrivateInvitations}
        setIsEmptyPrivateInvitations={setIsEmptyPrivateInvitations}
      />

      <DataRoomInvitations
        isTableView={isTableView}
        isEmpty={isEmptyDRInvitation}
        setIsEmptyDRInvitation={setIsEmptyDRInvitation}
      />

      {isEmptyPage && <PortfolioDealsEmptyState isBuyerSide />}
    </div>
  );
};

export default BuyerBrowsePortfolio;
